/**
 * 通知设置单点（任务 14.31c）：纯本地 localStorage `lhc.notify.v1`，无服务端接口
 * （旧版 notification_settings_service.dart 同口径，六个散键收编为一个 JSON）。
 *
 * 开关集对照旧版：消息提醒组 enabled/showPreview/mentionOnly 三开关照搬；
 * 「PC 端提醒」组收编 pcPopup（旧 in_app_popup）+ sound 两开关，仅桌面布局显示
 * （旧版 isPcRuntime 同语义）；旧 desktop_system 是 Windows 原生专属不做。
 *
 * 本轮只做存取与设置 UI；实际消费（Web Push / 页内提醒 / 声音）归任务 18。
 */

export const NOTIFY_STORAGE_KEY = 'lhc.notify.v1';

export interface NotifySettings {
  /** 接收新消息通知（总开关，关=全静默） */
  enabled: boolean;
  /** 通知里显示消息内容 */
  showPreview: boolean;
  /** 仅提醒 @ 我和私聊（群聊普通消息不提醒） */
  mentionOnly: boolean;
  /** 桌面：窗口内弹窗提示（旧 in_app_popup） */
  pcPopup: boolean;
  /** 桌面：通知声音 */
  sound: boolean;
}

/** 默认值对齐旧版 _load 的 fallback（desktop_system 除外，不做） */
export const DEFAULT_NOTIFY: NotifySettings = {
  enabled: true,
  showPreview: true,
  mentionOnly: false,
  pcPopup: true,
  sound: true,
};

export type NotifyKey = keyof NotifySettings;

/** 消息提醒组（两端都显示） */
export const NOTIFY_MESSAGE_SWITCHES: ReadonlyArray<{ key: NotifyKey; label: string; sub?: string }> = [
  { key: 'enabled', label: '接收新消息通知', sub: '关闭后，所有新消息提醒都会静默' },
  { key: 'showPreview', label: '显示消息内容' },
  { key: 'mentionOnly', label: '仅提醒 @ 我和私聊', sub: '群聊普通消息不再弹出提醒' },
];

/** PC 端提醒组（仅桌面布局显示，旧版 isPcRuntime 同语义） */
export const NOTIFY_PC_SWITCHES: ReadonlyArray<{ key: NotifyKey; label: string; sub?: string }> = [
  { key: 'pcPopup', label: '窗口内弹窗提示', sub: '收到新消息时在窗口右下角提示' },
  { key: 'sound', label: '通知声音' },
];

/** 容忍坏 JSON/缺字段/非布尔：逐字段回落默认 */
export function normalizeNotify(raw: unknown): NotifySettings {
  const src = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const pick = (key: NotifyKey): boolean =>
    typeof src[key] === 'boolean' ? (src[key] as boolean) : DEFAULT_NOTIFY[key];
  return {
    enabled: pick('enabled'),
    showPreview: pick('showPreview'),
    mentionOnly: pick('mentionOnly'),
    pcPopup: pick('pcPopup'),
    sound: pick('sound'),
  };
}

export function readNotifySettings(storage: Pick<Storage, 'getItem'>): NotifySettings {
  let raw: unknown = null;
  try {
    raw = JSON.parse(storage.getItem(NOTIFY_STORAGE_KEY) ?? 'null');
  } catch {
    raw = null;
  }
  return normalizeNotify(raw);
}

export function writeNotifySettings(storage: Pick<Storage, 'setItem'>, s: NotifySettings): void {
  try {
    storage.setItem(NOTIFY_STORAGE_KEY, JSON.stringify(s));
  } catch {
    // 存储满/隐私模式写失败：设置仅本次会话生效，不炸页面
  }
}
